import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Center,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import {
  FiActivity,
  FiAward,
  FiBookOpen,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiEye,
  FiMail,
  FiMoreVertical,
  FiRefreshCw,
  FiTarget,
  FiTrendingDown,
  FiTrendingUp,
  FiUsers,
  FiXCircle,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AdminLayout,
  Button,
  Card,
  DataTable,
  SearchInput,
  SelectInput,
  StatsCard,
} from '@grcs/components';
import { TableColumn } from '@grcs/components/types';

import { useAnalyticsService } from '@grcs/hook/useAnalyticsService';
import {
  EnhancedHomeworkStudentAnalytics,
  HomeworkAnalyticsData,
} from '@grcs/utils/analytics/AnalyticsService';
import { formatDate } from '@grcs/utils/formatting/formatDate';
import { formatMillis } from '@grcs/utils/formatting/formatMillis';

interface FilterOptions {
  completion: 'all' | 'completed' | 'pending';
  performance: 'all' | 'excellent' | 'good' | 'average' | 'poor';
  timeCategory: 'all' | 'fast' | 'average' | 'slow';
}

export const AnalyticsEnhanced: React.FC = () => {
  const { classId, homeworkId, year } = useParams();
  const navigate = useNavigate();
  const analyticsService = useAnalyticsService();

  const [analytics, setAnalytics] = useState<HomeworkAnalyticsData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [availableHomework, setAvailableHomework] = useState<any[]>([]);
  const [filters, setFilters] = useState<FilterOptions>({
    completion: 'all',
    performance: 'all',
    timeCategory: 'all',
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchAnalyticsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, homeworkId]);

  const fetchAnalyticsData = async (refresh = false) => {
    try {
      if (refresh) setIsRefreshing(true);
      else setIsLoading(true);

      console.log('🔍 Analytics Debug - Starting data fetch:', {
        homeworkId,
        classId,
      });

      const result = await analyticsService.getHomeworkAnalytics(
        homeworkId!,
        classId!,
      );
      setAvailableHomework(result.availableHomework);

      if (!result.analytics) {
        console.log('⚠️  Analytics Debug - No student data found');
        setAnalytics(null);
        return;
      }
      setAnalytics(result.analytics);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const filteredStudents = useMemo(() => {
    if (!analytics) return [];

    return analytics.students.filter((student) => {
      const matchesSearch = student.full_name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesCompletion =
        filters.completion === 'all' ||
        (filters.completion === 'completed' && student.completed) ||
        (filters.completion === 'pending' && !student.completed);
      const matchesPerformance =
        filters.performance === 'all' ||
        student.performance === filters.performance;
      const matchesTimeCategory =
        filters.timeCategory === 'all' ||
        student.timeCategory === filters.timeCategory;

      return (
        matchesSearch &&
        matchesCompletion &&
        matchesPerformance &&
        matchesTimeCategory
      );
    });
  }, [analytics, searchQuery, filters]);

  const handleExportData = () => {
    if (!analytics) return;

    const csvContent = [
      'Student Name,Completed,Completion %,Total Time,Completed At,Performance,Time Category',
      ...filteredStudents.map(
        (s) =>
          `"${s.full_name}",${
            s.completed ? 'Yes' : 'No'
          },${s.completionPercentage.toFixed(1)}%,${formatMillis(
            s.total_time,
          )},${formatDate(s.completed_at)},${s.performance},${s.timeCategory}`,
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `homework_${homeworkId}_analytics.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: TableColumn<EnhancedHomeworkStudentAnalytics>[] = [
    {
      key: 'student',
      header: 'Student',
      render: (student) => (
        <VStack align="start" spacing={1}>
          <Text fontWeight="medium">{student.full_name}</Text>
          <Badge
            colorScheme={student.completed ? 'green' : 'orange'}
            variant="subtle"
            size="sm"
          >
            {student.completed ? 'Completed' : 'Pending'}
          </Badge>
        </VStack>
      ),
    },
    {
      key: 'performance',
      header: 'Performance',
      render: (student) => (
        <VStack align="start" spacing={1}>
          <HStack>
            <Badge
              colorScheme={
                student.performance === 'excellent'
                  ? 'green'
                  : student.performance === 'good'
                  ? 'blue'
                  : student.performance === 'average'
                  ? 'yellow'
                  : 'red'
              }
              variant="solid"
            >
              {student.performance}
            </Badge>
            <Text fontSize="sm" fontWeight="medium">
              {student.completionPercentage.toFixed(1)}%
            </Text>
          </HStack>
          <Progress
            value={student.completionPercentage}
            size="sm"
            width="100px"
            colorScheme={
              student.performance === 'excellent'
                ? 'green'
                : student.performance === 'good'
                ? 'blue'
                : student.performance === 'average'
                ? 'yellow'
                : 'red'
            }
          />
        </VStack>
      ),
    },
    {
      key: 'time',
      header: 'Time Analysis',
      render: (student) => (
        <VStack align="start" spacing={1}>
          <HStack>
            <FiClock />
            <Text fontWeight="medium">{formatMillis(student.total_time)}</Text>
          </HStack>
          <Badge
            colorScheme={
              student.timeCategory === 'fast'
                ? 'green'
                : student.timeCategory === 'average'
                ? 'blue'
                : 'orange'
            }
            variant="subtle"
            size="sm"
          >
            {student.timeCategory} pace
          </Badge>
        </VStack>
      ),
    },
    {
      key: 'completion',
      header: 'Completion Details',
      render: (student) => (
        <VStack align="start" spacing={1}>
          <Text fontSize="sm">
            {student.completed_at
              ? formatDate(student.completed_at)
              : 'Not completed'}
          </Text>
          {student.completed ? (
            <HStack>
              <FiCheckCircle color="green" />
              <Text fontSize="sm" color="green.500">
                Complete
              </Text>
            </HStack>
          ) : (
            <HStack>
              <FiXCircle color="orange" />
              <Text fontSize="sm" color="orange.500">
                In Progress
              </Text>
            </HStack>
          )}
        </VStack>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (student) => (
        <Menu>
          <MenuButton
            as={IconButton}
            icon={<FiMoreVertical />}
            variant="ghost"
            size="sm"
          />
          <MenuList>
            <MenuItem
              icon={<FiEye />}
              onClick={() =>
                navigate(`/teacher/student/${student.student_token}/analytics`)
              }
            >
              View Details
            </MenuItem>
            <MenuItem
              icon={<FiMail />}
              onClick={() =>
                window.open(`mailto:${student.full_name}@school.edu`, '_blank')
              }
            >
              Contact Student
            </MenuItem>
          </MenuList>
        </Menu>
      ),
    },
  ];

  if (isLoading) {
    return (
      <AdminLayout>
        <Center py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Loading analytics data...</Text>
          </VStack>
        </Center>
      </AdminLayout>
    );
  }

  if (!analytics) {
    return (
      <AdminLayout>
        <VStack spacing={6} align="stretch">
          <Alert status="warning">
            <AlertIcon />
            <VStack align="start" spacing={2}>
              <Text fontWeight="bold">No Analytics Data Available</Text>
              <Text fontSize="sm">This could mean:</Text>
              <VStack align="start" spacing={1} fontSize="sm" color="gray.600">
                <Text>• No students have been assigned this homework yet</Text>
                <Text>• The homework ID ({homeworkId}) doesn't exist</Text>
                <Text>• Students haven't started working on this homework</Text>
              </VStack>
              {availableHomework.length > 0 && (
                <VStack align="start" spacing={2} mt={3}>
                  <Text fontSize="sm" fontWeight="medium">
                    Available homework for analytics:
                  </Text>
                  {availableHomework.slice(0, 3).map((hw) => (
                    <Button
                      key={hw.homework_token}
                      size="xs"
                      variant="outline"
                      onClick={() =>
                        navigate(
                          `/teacher/${year}/class/${classId}/analytics/${hw.homework_token}`,
                        )
                      }
                    >
                      {hw.title} ({formatDate(hw.created_at)})
                    </Button>
                  ))}
                  {availableHomework.length > 3 && (
                    <Text fontSize="xs" color="gray.500">
                      +{availableHomework.length - 3} more available
                    </Text>
                  )}
                </VStack>
              )}
              <HStack mt={3}>
                <Button
                  size="sm"
                  onClick={() =>
                    navigate(`/teacher/${year}/class/${classId}/homeworks`)
                  }
                >
                  Back to Homework List
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fetchAnalyticsData(true)}
                  isLoading={isRefreshing}
                >
                  Try Again
                </Button>
              </HStack>
            </VStack>
          </Alert>
        </VStack>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="2xl" fontWeight="bold">
              Homework Analytics
            </Text>
            <Text color="gray.600">
              Detailed analysis and insights for homework performance
            </Text>
          </Box>
          <HStack spacing={3}>
            <IconButton
              aria-label="Refresh data"
              icon={<FiRefreshCw />}
              variant="secondary"
              isLoading={isRefreshing}
              onClick={() => fetchAnalyticsData(true)}
            />
            <Button
              leftIcon={<FiDownload />}
              variant="secondary"
              onClick={handleExportData}
              size="sm"
            >
              Export
            </Button>
          </HStack>
        </HStack>

        {/* Key Metrics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <StatsCard
            title="Completion Rate"
            value={`${analytics.completionRate.toFixed(1)}%`}
            icon={FiTarget}
          />
          <StatsCard
            title="Average Score"
            value={`${analytics.averageScore.toFixed(1)}%`}
            icon={FiAward}
          />
          <StatsCard
            title="Average Time"
            value={formatMillis(analytics.averageTime)}
            icon={FiClock}
          />
          <StatsCard
            title="Total Students"
            value={analytics.students.length.toString()}
            icon={FiUsers}
          />
        </SimpleGrid>

        {/* Main Content */}
        <Card>
          <Tabs>
            <TabList>
              <Tab>Student Performance</Tab>
              <Tab>Question Analysis</Tab>
              <Tab>Time Distribution</Tab>
              <Tab>Insights & Trends</Tab>
            </TabList>

            <TabPanels>
              {/* Student Performance Tab */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  {/* Filters */}
                  <HStack justify="space-between" wrap="wrap" spacing={4}>
                    <HStack spacing={3}>
                      <SearchInput
                        placeholder="Search students..."
                        value={searchQuery}
                        onChange={setSearchQuery}
                      />
                      <SelectInput
                        value={filters.completion}
                        onChange={(value) =>
                          setFilters((prev) => ({
                            ...prev,
                            completion: value as any,
                          }))
                        }
                        options={[
                          { label: 'All Students', value: 'all' },
                          { label: 'Completed Only', value: 'completed' },
                          { label: 'Pending Only', value: 'pending' },
                        ]}
                      />
                      <SelectInput
                        value={filters.performance}
                        onChange={(value) =>
                          setFilters((prev) => ({
                            ...prev,
                            performance: value as any,
                          }))
                        }
                        options={[
                          { label: 'All Performance', value: 'all' },
                          { label: 'Excellent', value: 'excellent' },
                          { label: 'Good', value: 'good' },
                          { label: 'Average', value: 'average' },
                          { label: 'Poor', value: 'poor' },
                        ]}
                      />
                    </HStack>
                  </HStack>

                  {/* Performance Distribution */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Card variant="outline">
                      <VStack spacing={3}>
                        <HStack>
                          <FiTrendingUp color="green" />
                          <Text fontWeight="semibold" color="green.500">
                            High Performers
                          </Text>
                        </HStack>
                        <Text fontSize="2xl" fontWeight="bold">
                          {
                            analytics.students.filter(
                              (s) =>
                                s.performance === 'excellent' ||
                                s.performance === 'good',
                            ).length
                          }
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Students scoring ≥75%
                        </Text>
                      </VStack>
                    </Card>

                    <Card variant="outline">
                      <VStack spacing={3}>
                        <HStack>
                          <FiActivity color="blue" />
                          <Text fontWeight="semibold" color="blue.500">
                            Average Performers
                          </Text>
                        </HStack>
                        <Text fontSize="2xl" fontWeight="bold">
                          {
                            analytics.students.filter(
                              (s) => s.performance === 'average',
                            ).length
                          }
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Students scoring 60-74%
                        </Text>
                      </VStack>
                    </Card>

                    <Card variant="outline">
                      <VStack spacing={3}>
                        <HStack>
                          <FiTrendingDown color="red" />
                          <Text fontWeight="semibold" color="red.500">
                            Need Support
                          </Text>
                        </HStack>
                        <Text fontSize="2xl" fontWeight="bold">
                          {
                            analytics.students.filter(
                              (s) => s.performance === 'poor',
                            ).length
                          }
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Students scoring &lt;60%
                        </Text>
                      </VStack>
                    </Card>
                  </SimpleGrid>

                  {/* Student Table */}
                  <DataTable data={filteredStudents} columns={columns} />
                </VStack>
              </TabPanel>

              {/* Question Analysis Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <Text fontSize="lg" fontWeight="semibold">
                    Question-by-Question Analysis
                  </Text>

                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                    {analytics.questionAnalytics.map((qa) => (
                      <Card key={qa.questionNumber} variant="outline">
                        <VStack align="stretch" spacing={3}>
                          <HStack justify="space-between">
                            <Text fontWeight="bold">
                              Question {qa.questionNumber}
                            </Text>
                            <Badge
                              colorScheme={
                                qa.difficulty === 'easy'
                                  ? 'green'
                                  : qa.difficulty === 'medium'
                                  ? 'yellow'
                                  : 'red'
                              }
                              variant="subtle"
                            >
                              {qa.difficulty}
                            </Badge>
                          </HStack>

                          <VStack align="start" spacing={2}>
                            <HStack justify="space-between" width="100%">
                              <Text fontSize="sm">Success Rate:</Text>
                              <Text fontWeight="medium">
                                {(
                                  (qa.correctAnswers / qa.totalAttempts) *
                                  100
                                ).toFixed(1)}
                                %
                              </Text>
                            </HStack>
                            <Progress
                              value={
                                (qa.correctAnswers / qa.totalAttempts) * 100
                              }
                              size="sm"
                              width="100%"
                              colorScheme={
                                qa.correctAnswers / qa.totalAttempts >= 0.8
                                  ? 'green'
                                  : qa.correctAnswers / qa.totalAttempts >= 0.6
                                  ? 'yellow'
                                  : 'red'
                              }
                            />
                            <HStack justify="space-between" width="100%">
                              <Text fontSize="sm">Attempts:</Text>
                              <Text fontSize="sm">
                                {qa.correctAnswers}/{qa.totalAttempts}
                              </Text>
                            </HStack>
                          </VStack>
                        </VStack>
                      </Card>
                    ))}
                  </SimpleGrid>
                </VStack>
              </TabPanel>

              {/* Time Distribution Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <Text fontSize="lg" fontWeight="semibold">
                    Time Analysis and Distribution
                  </Text>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <StatsCard
                      title="Fast Completers"
                      value={analytics.timeDistribution.fast.toString()}
                      icon={FiTrendingUp}
                    />
                    <StatsCard
                      title="Average Pace"
                      value={analytics.timeDistribution.average.toString()}
                      icon={FiActivity}
                    />
                    <StatsCard
                      title="Slow Completers"
                      value={analytics.timeDistribution.slow.toString()}
                      icon={FiClock}
                    />
                  </SimpleGrid>

                  <Alert status="info">
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="semibold">
                        Time Category Definitions:
                      </Text>
                      <Text fontSize="sm">
                        • Fast: Completed 30% faster than average (
                        {formatMillis(analytics.averageTime * 0.7)})
                      </Text>
                      <Text fontSize="sm">
                        • Average: Within 30% of average time (
                        {formatMillis(analytics.averageTime)})
                      </Text>
                      <Text fontSize="sm">
                        • Slow: Took 30% longer than average (
                        {formatMillis(analytics.averageTime * 1.3)})
                      </Text>
                    </VStack>
                  </Alert>
                </VStack>
              </TabPanel>

              {/* Insights Tab */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <Text fontSize="lg" fontWeight="semibold">
                    Insights and Recommendations
                  </Text>

                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {/* Top Performers */}
                    <Card>
                      <VStack align="stretch" spacing={4}>
                        <HStack>
                          <FiAward color="gold" />
                          <Text fontWeight="semibold">Top Performers</Text>
                        </HStack>
                        {analytics.students
                          .filter((s) => s.performance === 'excellent')
                          .sort(
                            (a, b) =>
                              b.completionPercentage - a.completionPercentage,
                          )
                          .slice(0, 5)
                          .map((student, index) => (
                            <HStack
                              key={student.student_token}
                              justify="space-between"
                            >
                              <HStack>
                                <Badge colorScheme="gold" variant="solid">
                                  #{index + 1}
                                </Badge>
                                <Text fontWeight="medium">
                                  {student.full_name}
                                </Text>
                              </HStack>
                              <Text fontWeight="bold" color="green.500">
                                {student.completionPercentage.toFixed(1)}%
                              </Text>
                            </HStack>
                          ))}
                      </VStack>
                    </Card>

                    {/* Students Needing Help */}
                    <Card>
                      <VStack align="stretch" spacing={4}>
                        <HStack>
                          <FiTarget color="red" />
                          <Text fontWeight="semibold">
                            Students Needing Support
                          </Text>
                        </HStack>
                        {analytics.students
                          .filter((s) => s.performance === 'poor')
                          .sort(
                            (a, b) =>
                              a.completionPercentage - b.completionPercentage,
                          )
                          .slice(0, 5)
                          .map((student) => (
                            <HStack
                              key={student.student_token}
                              justify="space-between"
                            >
                              <VStack align="start" spacing={0}>
                                <Text fontWeight="medium">
                                  {student.full_name}
                                </Text>
                                <Text fontSize="sm" color="gray.500">
                                  {student.completed
                                    ? 'Completed'
                                    : 'In Progress'}
                                </Text>
                              </VStack>
                              <Badge colorScheme="red" variant="subtle">
                                {student.completionPercentage.toFixed(1)}%
                              </Badge>
                            </HStack>
                          ))}
                      </VStack>
                    </Card>
                  </SimpleGrid>

                  {/* Recommendations */}
                  <Card bg="blue.50" borderColor="blue.200">
                    <VStack align="start" spacing={3}>
                      <HStack>
                        <FiBookOpen color="blue" />
                        <Text fontWeight="semibold" color="blue.700">
                          Teaching Recommendations
                        </Text>
                      </HStack>

                      <VStack align="start" spacing={2}>
                        {analytics.completionRate < 70 && (
                          <Text fontSize="sm">
                            • Low completion rate suggests the homework may be
                            too challenging. Consider providing additional
                            support materials.
                          </Text>
                        )}
                        {analytics.averageScore < 60 && (
                          <Text fontSize="sm">
                            • Average scores are low. Review the most
                            challenging questions and provide targeted
                            instruction.
                          </Text>
                        )}
                        {analytics.students.filter(
                          (s) => s.timeCategory === 'slow',
                        ).length >
                          analytics.students.length * 0.3 && (
                          <Text fontSize="sm">
                            • Many students are taking longer than expected.
                            Consider breaking down complex questions or
                            providing time management tips.
                          </Text>
                        )}
                        {analytics.students.filter((s) => !s.completed).length >
                          0 && (
                          <Text fontSize="sm">
                            •{' '}
                            {
                              analytics.students.filter((s) => !s.completed)
                                .length
                            }{' '}
                            students haven't completed the homework yet.
                            Consider sending reminders or offering additional
                            time.
                          </Text>
                        )}
                      </VStack>
                    </VStack>
                  </Card>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Card>
      </VStack>
    </AdminLayout>
  );
};
